// components/event/EventsSection.js
import React, { useState, useEffect } from 'react';
import { Plus, X, Pencil, Trash2 } from 'lucide-react';

import { eventsAPI } from '../../api/events';
import { useAuth } from '../../contexts/AuthContext';
import EventCard from './EventCard';
import EventFilters from './EventFilters';
import ConfirmActionModal from '../shared/ConfirmActionModal';

const defaultEventForm = {
  title: '',
  event_date: '',
  description: '',
  website_summary: '',
  event_type: 'RUN',
  event_region: 'LOCAL',
  is_competition: true,
  event_url: '',
  external_image_url: '',
  image: null,
  coupon_code: '',
  discount_amount: '',
  distances_text: '',
  location_name: '',
  location_address: '',
  location_city: '',
};

const formatDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const isUpcomingByDate = (value) => {
  if (!value) return false;
  const eventDate = new Date(value);
  if (Number.isNaN(eventDate.getTime())) return false;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const eventDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  return eventDay >= startOfToday;
};

const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [selectedType, setSelectedType] = useState('ALL');
  const [selectedDistance, setSelectedDistance] = useState('ALL');
  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [subscriptionLoadingEventId, setSubscriptionLoadingEventId] = useState(null);

  const { isAdmin, isAuthenticated } = useAuth();
  const [showFormModal, setShowFormModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventForm, setEventForm] = useState(defaultEventForm);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
    payload: null,
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackEvent, setFeedbackEvent] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    achieved_goals: false,
    competition_rating: 5,
    preparation_rating: 5,
    review_text: '',
  });

  const fetchEvents = async () => {
    try {
      const data = await eventsAPI.getEvents();
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const normalizedDistanceOptions = Array.from(
    new Set(
      events
        .flatMap((event) => (Array.isArray(event.distances) ? event.distances : []))
        .map((distance) => distance && String(distance).trim())
        .filter(Boolean),
    ),
  );

  const filteredEvents = events.filter((event) => {
    const dateOk = isUpcomingByDate(event.event_date);
    const typeOk = selectedType === 'ALL' || event.event_type === selectedType;
    const distanceOk =
      selectedDistance === 'ALL' ||
      (Array.isArray(event.distances) && event.distances.includes(selectedDistance));
    const regionOk = selectedRegion === 'ALL' || event.event_region === selectedRegion;
    return dateOk && typeOk && distanceOk && regionOk;
  });

  const localEvents = filteredEvents.filter((event) => event.event_region !== 'INTL');
  const internationalEvents = filteredEvents.filter((event) => event.event_region === 'INTL');

  const resetForm = () => {
    setEditingEventId(null);
    setEventForm(defaultEventForm);
  };

  const openCreateModal = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditModal = (event) => {
    setEditingEventId(event.id);
    setEventForm({
      title: event.title || '',
      event_date: formatDateTimeLocal(event.event_date),
      description: event.description || '',
      website_summary: event.website_summary || '',
      event_type: event.event_type || 'RUN',
      event_region: event.event_region || 'LOCAL',
      is_competition: !!event.is_competition,
      event_url: event.event_url || '',
      external_image_url: event.raw_image_url || '',
      image: null,
      coupon_code: event.coupon_code || '',
      discount_amount: event.discount_amount ?? '',
      distances_text: Array.isArray(event.distances) ? event.distances.join(', ') : '',
      location_name: event.location?.name || '',
      location_address: event.location?.address || '',
      location_city: event.location?.city || '',
    });
    setShowFormModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await eventsAPI.deleteEvent(eventId);
      setLoading(true);
      await fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      window.alert('Не удалось удалить событие.');
    }
  };

  const handleSubmitEvent = async () => {
    if (!eventForm.title || !eventForm.event_date || !eventForm.description) {
      window.alert('Заполните обязательные поля: название, дата, описание.');
      return;
    }

    const distances = eventForm.distances_text
      .split(/[\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    const basePayload = {
      title: eventForm.title,
      event_date: eventForm.event_date,
      description: eventForm.description,
      website_summary: eventForm.website_summary || '',
      event_type: eventForm.event_type,
      event_region: eventForm.event_region,
      is_competition: !!eventForm.is_competition,
      event_url: eventForm.event_url || null,
      external_image_url: eventForm.external_image_url || null,
      coupon_code: eventForm.coupon_code || null,
      discount_amount: eventForm.discount_amount ? Number(eventForm.discount_amount) : null,
      distances,
      location_name: eventForm.location_name || '',
      location_address: eventForm.location_address || '',
      location_city: eventForm.location_city || '',
    };
    const hasImageFile = eventForm.image instanceof File;
    const payload = hasImageFile ? new FormData() : basePayload;
    if (hasImageFile) {
      Object.entries(basePayload).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value)) {
          value.forEach((distance) => payload.append('distances', distance));
          return;
        }
        payload.append(key, String(value));
      });
      payload.append('image', eventForm.image);
    }

    try {
      setIsSubmitting(true);
      if (editingEventId) {
        await eventsAPI.updateEvent(editingEventId, payload);
      } else {
        await eventsAPI.createEvent(payload);
      }
      setShowFormModal(false);
      resetForm();
      setLoading(true);
      await fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      window.alert('Не удалось сохранить событие. Проверьте поля формы.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openConfirm = ({ title, description, actionType, payload }) => {
    setConfirmModal({ isOpen: true, title, description, actionType, payload });
  };

  const closeConfirm = () => {
    setConfirmModal({ isOpen: false, title: '', description: '', actionType: null, payload: null });
  };

  const confirmAction = async () => {
    const { actionType, payload } = confirmModal;
    closeConfirm();
    if (actionType === 'delete' && payload?.eventId) {
      await handleDeleteEvent(payload.eventId);
      return;
    }
    if (actionType === 'save') {
      await handleSubmitEvent();
    }
  };

  const handleToggleEventSubscription = async (event) => {
    if (!isAuthenticated) {
      window.alert('Войдите в аккаунт, чтобы записаться на событие.');
      return;
    }

    try {
      setSubscriptionLoadingEventId(event.id);
      const updatedEvent = event.is_subscribed
        ? await eventsAPI.unsubscribeEvent(event.id)
        : await eventsAPI.subscribeEvent(event.id);

      setEvents((prev) => prev.map((item) => (item.id === event.id ? updatedEvent : item)));
    } catch (error) {
      console.error('Error updating event subscription:', error);
      window.alert('Не удалось обновить запись на событие.');
    } finally {
      setSubscriptionLoadingEventId(null);
    }
  };

  const pastSubscribedEvents = events
    .filter((event) => !isUpcomingByDate(event.event_date) && event.is_subscribed)
    .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));

  const openFeedbackModal = async (event) => {
    setFeedbackEvent(event);
    setShowFeedbackModal(true);
    setFeedbackForm({
      achieved_goals: false,
      competition_rating: 5,
      preparation_rating: 5,
      review_text: '',
    });

    try {
      const response = await eventsAPI.getEventFeedback(event.id);
      const existing = response?.feedback || response;
      if (existing && existing.id) {
        setFeedbackForm({
          achieved_goals: Boolean(existing.achieved_goals),
          competition_rating: Number(existing.competition_rating || 5),
          preparation_rating: Number(existing.preparation_rating || 5),
          review_text: existing.review_text || '',
        });
      }
    } catch (error) {
      console.error('Error loading event feedback:', error);
    }
  };

  const submitEventFeedback = async () => {
    if (!feedbackEvent?.id) return;
    if (!String(feedbackForm.review_text || '').trim()) {
      window.alert('Добавьте текст отзыва');
      return;
    }

    try {
      setFeedbackSubmitting(true);
      await eventsAPI.submitEventFeedback(feedbackEvent.id, {
        achieved_goals: Boolean(feedbackForm.achieved_goals),
        competition_rating: Number(feedbackForm.competition_rating),
        preparation_rating: Number(feedbackForm.preparation_rating),
        review_text: String(feedbackForm.review_text || '').trim(),
      });
      window.alert('Спасибо! Отзыв отправлен на модерацию.');
      setShowFeedbackModal(false);
      setFeedbackEvent(null);
    } catch (error) {
      console.error('Error submitting event feedback:', error);
      const message = error?.response?.data?.error || 'Не удалось отправить отзыв.';
      window.alert(message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const renderEventCard = (event) => (
    <div key={event.id} className="relative">
      <EventCard
        event={event}
        isAuthenticated={isAuthenticated}
        onToggleSubscription={handleToggleEventSubscription}
        subscriptionLoading={subscriptionLoadingEventId === event.id}
      />
      {isAdmin && (
        <div className="absolute top-2 right-2 z-20 flex gap-2">
          <button
            type="button"
            onClick={() => openEditModal(event)}
            className="p-2 rounded-full bg-white/90 text-blue-700 hover:bg-blue-50 shadow"
            title="Редактировать событие"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => openConfirm({
              title: 'Подтвердите удаление',
              description: 'Удалить событие?',
              actionType: 'delete',
              payload: { eventId: event.id },
            })}
            className="p-2 rounded-full bg-white/90 text-red-600 hover:bg-red-50 shadow"
            title="Удалить событие"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
        <div className="max-w-[1360px] mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20 sm:pt-24 pb-8 sm:pb-12 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
      <div className="max-w-[1360px] mx-auto">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-800">События</h1>
          {isAdmin && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]"
            >
              <Plus size={16} />
              Добавить событие
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6 lg:gap-8">
          <aside className="lg:sticky lg:top-24 self-start">
            <EventFilters
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedDistance={selectedDistance}
              onDistanceChange={setSelectedDistance}
              distanceOptions={normalizedDistanceOptions}
              selectedRegion={selectedRegion}
              onRegionChange={setSelectedRegion}
            />
          </aside>

          <div>
            {localEvents.length > 0 && (
              <div className="mb-10">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Локальные старты (Израиль)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {localEvents.map(renderEventCard)}
                </div>
              </div>
            )}

            {internationalEvents.length > 0 && (
              <div className="mb-10">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Международные старты</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {internationalEvents.map(renderEventCard)}
                </div>
              </div>
            )}

            {filteredEvents.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <p className="text-gray-600">Нет доступных событий</p>
              </div>
            )}

            {isAuthenticated && pastSubscribedEvents.length > 0 && (
              <div className="mt-10">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Прошедшие события: ваш итог и отзыв</h2>
                <div className="space-y-3">
                  {pastSubscribedEvents.map((event) => (
                    <div key={`past-${event.id}`} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
                      <div>
                        <p className="font-semibold text-gray-900">{event.title}</p>
                        <p className="text-sm text-gray-500">{new Date(event.event_date).toLocaleString('ru-RU')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openFeedbackModal(event)}
                        className="px-4 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]"
                      >
                        Оставить итог/отзыв
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowFormModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">{editingEventId ? 'Редактировать событие' : 'Новое событие'}</h2>
              <button onClick={() => setShowFormModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                openConfirm({
                  title: editingEventId ? 'Подтвердите изменение' : 'Подтвердите добавление',
                  description: editingEventId ? 'Сохранить изменения события?' : 'Добавить новое событие?',
                  actionType: 'save',
                  payload: null,
                });
              }}
              className="p-6 space-y-4"
            >
              <input className="w-full border rounded-lg px-3 py-2" placeholder="Название *" value={eventForm.title} onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))} />
              <input className="w-full border rounded-lg px-3 py-2" type="datetime-local" value={eventForm.event_date} onChange={(e) => setEventForm((prev) => ({ ...prev, event_date: e.target.value }))} />
              <textarea className="w-full border rounded-lg px-3 py-2" rows={4} placeholder="Описание *" value={eventForm.description} onChange={(e) => setEventForm((prev) => ({ ...prev, description: e.target.value }))} />
              <textarea className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Краткое описание" value={eventForm.website_summary} onChange={(e) => setEventForm((prev) => ({ ...prev, website_summary: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className="border rounded-lg px-3 py-2" value={eventForm.event_type} onChange={(e) => setEventForm((prev) => ({ ...prev, event_type: e.target.value }))}>
                  <option value="RUN">Бег</option>
                  <option value="TRI">Триатлон</option>
                  <option value="SWIM">Плавание</option>
                </select>
                <select className="border rounded-lg px-3 py-2" value={eventForm.event_region} onChange={(e) => setEventForm((prev) => ({ ...prev, event_region: e.target.value }))}>
                  <option value="LOCAL">Локальный (Израиль)</option>
                  <option value="INTL">Международный</option>
                </select>
              </div>

              <input className="w-full border rounded-lg px-3 py-2" placeholder="Ссылка на событие" value={eventForm.event_url} onChange={(e) => setEventForm((prev) => ({ ...prev, event_url: e.target.value }))} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="Ссылка на картинку"
                  value={eventForm.external_image_url}
                  onChange={(e) => setEventForm((prev) => ({ ...prev, external_image_url: e.target.value }))}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEventForm((prev) => ({ ...prev, image: e.target.files?.[0] || null }))}
                />
              </div>
              <p className="text-xs text-gray-500">Приоритет отображения: загруженный файл, затем ссылка.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input className="border rounded-lg px-3 py-2" placeholder="Промокод" value={eventForm.coupon_code} onChange={(e) => setEventForm((prev) => ({ ...prev, coupon_code: e.target.value }))} />
                <input className="border rounded-lg px-3 py-2" placeholder="Скидка %" type="number" min="0" value={eventForm.discount_amount} onChange={(e) => setEventForm((prev) => ({ ...prev, discount_amount: e.target.value }))} />
              </div>

              <textarea className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Дистанции (через запятую или с новой строки)" value={eventForm.distances_text} onChange={(e) => setEventForm((prev) => ({ ...prev, distances_text: e.target.value }))} />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input className="border rounded-lg px-3 py-2" placeholder="Локация" value={eventForm.location_name} onChange={(e) => setEventForm((prev) => ({ ...prev, location_name: e.target.value }))} />
                <input className="border rounded-lg px-3 py-2" placeholder="Адрес" value={eventForm.location_address} onChange={(e) => setEventForm((prev) => ({ ...prev, location_address: e.target.value }))} />
                <input className="border rounded-lg px-3 py-2" placeholder="Город" value={eventForm.location_city} onChange={(e) => setEventForm((prev) => ({ ...prev, location_city: e.target.value }))} />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={eventForm.is_competition} onChange={(e) => setEventForm((prev) => ({ ...prev, is_competition: e.target.checked }))} />
                Соревновательное событие
              </label>

              <button disabled={isSubmitting} type="submit" className="w-full py-3 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] disabled:opacity-60">
                {isSubmitting ? 'Сохраняем...' : editingEventId ? 'Сохранить изменения' : 'Добавить событие'}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        description={confirmModal.description}
        onConfirm={confirmAction}
        onCancel={closeConfirm}
        loading={isSubmitting}
      />

      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowFeedbackModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Итог после события</h2>
              <button onClick={() => setShowFeedbackModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">{feedbackEvent?.title}</p>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={feedbackForm.achieved_goals}
                  onChange={(e) => setFeedbackForm((prev) => ({ ...prev, achieved_goals: e.target.checked }))}
                />
                Достиг(ла) поставленных целей
              </label>
              <label className="block text-sm text-gray-700">
                Оценка соревнования (1-5)
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={feedbackForm.competition_rating}
                  onChange={(e) => setFeedbackForm((prev) => ({ ...prev, competition_rating: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                />
              </label>
              <label className="block text-sm text-gray-700">
                Оценка подготовки (1-5)
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={feedbackForm.preparation_rating}
                  onChange={(e) => setFeedbackForm((prev) => ({ ...prev, preparation_rating: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                />
              </label>
              <label className="block text-sm text-gray-700">
                Отзыв и выводы
                <textarea
                  rows={4}
                  value={feedbackForm.review_text}
                  onChange={(e) => setFeedbackForm((prev) => ({ ...prev, review_text: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  placeholder="Как прошёл старт, что получилось, какие выводы сделали..."
                />
              </label>
            </div>
            <div className="border-t px-6 py-4 flex gap-2">
              <button
                disabled={feedbackSubmitting}
                onClick={submitEventFeedback}
                className="px-4 py-2 rounded-lg text-white bg-[#4897d2] disabled:opacity-60"
              >
                {feedbackSubmitting ? 'Отправка...' : 'Отправить на модерацию'}
              </button>
              <button onClick={() => setShowFeedbackModal(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsSection;
